import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { tracked } from '@glimmer/tracking';
import { Type } from '@warp-drive/core-types/symbols';
import type { TotalRevenueBreakdownItem } from 'lumen/models';
import type { HoteldateTransformReturn } from 'lumen/transforms/hoteldate';
import { REFERENCE_PERIOD_TRANSLATIONS_SHORT } from 'lumen/utils/constants/total-revenue-constants';

export default class TotalRevenueBreakdownReference extends Model {
  [Type] = 'total-revenue/breakdown-reference' as const;

  @belongsTo('total-revenue/breakdown-item', {
    async: false,
    inverse: 'referenceValues',
  })
  declare totalRevenueItem: TotalRevenueBreakdownItem;

  @attr('hoteldate')
  declare referenceFromDate: NonNullable<HoteldateTransformReturn>;
  @attr declare referenceType: string;
  @attr('number') declare value: number;

  @service declare intl: Services['intl'];
  @service declare totalRevenue: Services['total-revenue'];

  @tracked capacity?: number;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get change() {
    return this.totalRevenue.calculateChange(
      this.totalRevenueItem.valueOrPAR,
      this.valueOrPAR,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedChange() {
    return this.totalRevenue.formatChange(this.change);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedRelativeChange() {
    return this.totalRevenue.formatRelativeChange(this.relativeChange);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedValue() {
    return this.totalRevenue.formatValue(this.valueOrPAR);
  }

  /**
   * Returns true if either the current or reference value is negative.
   * Relative change does not make sense in this case.
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRelativeChangeUnavailable() {
    return (
      (this.totalRevenueItem.valueOrPAR ?? 0) < 0 || (this.valueOrPAR ?? 0) < 0
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get relativeChange() {
    return this.totalRevenue.calculateRelativeChange(
      this.totalRevenueItem.valueOrPAR,
      this.valueOrPAR,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get translatedReferenceType() {
    return this.intl.t(
      REFERENCE_PERIOD_TRANSLATIONS_SHORT[
        this.referenceType as keyof typeof REFERENCE_PERIOD_TRANSLATIONS_SHORT
      ],
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get valueOrPAR() {
    return this.totalRevenue.valueOrPAR(this.value ?? 0, this.capacity);
  }
}
